// apps/web/src/features/commercial-proposals/components/CommercialProposalEditorFields.tsx
//
// @aiPurpose the grouped editable field set for the commercial-proposal
//   editor (#1198), mirroring the prototype groups: header/rates, product,
//   buyout (incl. the 3-way payment scheme), logistics, advanced
//   coefficients, notes, and the manager signature. Pure presentational —
//   reads the form state + a single-field setter from the host.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COMMERCIAL_PROPOSAL_PAYMENT_SCHEMES,
  COMMERCIAL_PROPOSAL_TOTAL_CURRENCIES,
  CommercialProposalTotalCurrencySchema,
  type CommercialProposalTotalCurrency,
} from '@magimp/shared';

import { FormRow, Input, Select, Textarea } from '../../../ui/index.js';
import type { CommercialProposalFormState } from '../formModel.js';
import { CommercialProposalModeControl } from './CommercialProposalModeControl.js';
import { CommercialProposalPhotoControl } from './CommercialProposalPhotoControl.js';
import { CommercialProposalTariffUnitControl } from './CommercialProposalTariffUnitControl.js';

type SetField = <K extends keyof CommercialProposalFormState>(
  key: K,
  value: CommercialProposalFormState[K],
) => void;

export function CommercialProposalEditorFields({
  form,
  setField,
  proposalId,
  savedPhotoObjectKey,
  onPhotoUploadingChange,
}: {
  form: CommercialProposalFormState;
  setField: SetField;
  /** Proposal id when editing a saved draft; null in the create host. */
  proposalId: string | null;
  /** Photo key the proposal was loaded with — drives saved-vs-local preview. */
  savedPhotoObjectKey: string;
  /** Forwarded to the photo control so the body can block submit mid-upload. */
  onPhotoUploadingChange: (uploading: boolean) => void;
}) {
  const { t } = useTranslation('commercialProposals');
  const text = (key: keyof CommercialProposalFormState, type: string = 'text') => (
    <Input
      type={type}
      value={form[key] as string}
      onChange={(v) => setField(key, v as CommercialProposalFormState[typeof key])}
    />
  );

  // #1410: gate the section-specific fields by mode. Header/product, rates,
  // goodsCny, paymentScheme, the manager block, and the advanced divisor/rate
  // trio are ALWAYS visible; only the section EXTRA fields are gated. Hidden
  // fields are NOT cleared from form state (calc/projection do the gating).
  const showBuyout = form.mode !== 'LOGISTICS';
  const showLogistics = form.mode !== 'BUYOUT';

  return (
    <>
      <Group title={t('editor.fields.mode')}>
        <FormRow label={t('editor.fields.mode')}>
          <CommercialProposalModeControl value={form.mode} onChange={(v) => setField('mode', v)} />
        </FormRow>
      </Group>

      <Group title={t('editor.groups.header')}>
        <FormRow label={t('editor.fields.cnyRateRub')}>{text('cnyRateRub')}</FormRow>
        <FormRow label={t('editor.fields.usdRateRub')}>{text('usdRateRub')}</FormRow>
        <FormRow label={t('editor.fields.client')}>{text('client')}</FormRow>
      </Group>

      <Group title={t('editor.groups.product')}>
        <FormRow label={t('editor.fields.productName')}>{text('productName')}</FormRow>
        <FormRow label={t('editor.fields.characteristics')}>
          <Textarea
            value={form.characteristicsText}
            onChange={(v) => setField('characteristicsText', v)}
            rows={4}
          />
          <Hint text={t('editor.characteristicsHint')} />
        </FormRow>
        <FormRow label={t('editor.fields.photo')}>
          <CommercialProposalPhotoControl
            proposalId={proposalId}
            savedPhotoObjectKey={savedPhotoObjectKey}
            value={form.photoObjectKey}
            onChange={(key) => setField('photoObjectKey', key)}
            onUploadingChange={onPhotoUploadingChange}
          />
        </FormRow>
        {/* goodsCny + paymentScheme apply in EVERY mode (#1410): relocated out
            of the buyout-only group so they render in LOGISTICS too. */}
        <FormRow label={t('editor.fields.goodsCny')}>{text('goodsCny')}</FormRow>
        <FormRow label={t('editor.fields.paymentScheme')}>
          <PaymentSchemeControl
            value={form.paymentScheme}
            onChange={(v) => setField('paymentScheme', v)}
          />
        </FormRow>
      </Group>

      {showBuyout ? (
        <Group title={t('editor.groups.buyout')}>
          <FormRow label={t('editor.fields.buyoutTotalCurrency')}>
            <TotalCurrencySelect
              value={form.buyoutTotalCurrency}
              onChange={(value) => setField('buyoutTotalCurrency', value)}
            />
          </FormRow>
          <FormRow label={t('editor.fields.buyoutTerm')}>{text('buyoutTerm')}</FormRow>
          <FormRow label={t('editor.fields.chinaDeliveryCny')}>{text('chinaDeliveryCny')}</FormRow>
          <FormRow label={t('editor.fields.servicePercent')}>{text('servicePercent')}</FormRow>
          <FormRow label={t('editor.fields.buyoutNotes')}>
            <Textarea
              value={form.buyoutNotes}
              onChange={(v) => setField('buyoutNotes', v)}
              rows={3}
            />
          </FormRow>
        </Group>
      ) : null}

      {showLogistics ? (
        <Group title={t('editor.groups.logistics')}>
          <FormRow label={t('editor.fields.logisticsTotalCurrency')}>
            <TotalCurrencySelect
              value={form.logisticsTotalCurrency}
              onChange={(value) => setField('logisticsTotalCurrency', value)}
            />
          </FormRow>
          <FormRow label={t('editor.fields.destination')}>{text('destination')}</FormRow>
          <FormRow label={t('editor.fields.logisticsTerm')}>{text('logisticsTerm')}</FormRow>
          <FormRow label={t('editor.fields.weightKg')}>{text('weightKg')}</FormRow>
          <FormRow label={t('editor.fields.volumeM3')}>{text('volumeM3')}</FormRow>
          <FormRow label={t('editor.fields.tariffUsd')}>{text('tariffUsdPerKg')}</FormRow>
          <FormRow label={t('editor.fields.tariffUnit')}>
            <CommercialProposalTariffUnitControl
              value={form.tariffUnit}
              onChange={(v) => setField('tariffUnit', v)}
            />
          </FormRow>
          <FormRow label={t('editor.fields.packagingUsd')}>{text('packagingUsd')}</FormRow>
          <FormRow label={t('editor.fields.prrUsd')}>{text('prrUsd')}</FormRow>
          <FormRow label={t('editor.fields.insurancePercent')}>{text('insurancePercent')}</FormRow>
          <FormRow label={t('editor.fields.logisticsNotes')}>
            <Textarea
              value={form.logisticsNotes}
              onChange={(v) => setField('logisticsNotes', v)}
              rows={3}
            />
          </FormRow>
        </Group>
      ) : null}

      <Group title={t('editor.groups.totals')}>
        <FormRow label={t('editor.fields.grandTotalCurrency')}>
          <TotalCurrencySelect
            value={form.grandTotalCurrency}
            onChange={(value) => setField('grandTotalCurrency', value)}
          />
        </FormRow>
      </Group>

      <Group title={t('editor.groups.advanced')}>
        <FormRow label={t('editor.fields.vatDivisor')}>{text('vatDivisor')}</FormRow>
        <FormRow label={t('editor.fields.noVatDivisor')}>{text('noVatDivisor')}</FormRow>
        <FormRow label={t('editor.fields.insuranceCnyPerUsdRate')}>
          {text('insuranceCnyPerUsdRate')}
        </FormRow>
      </Group>

      <Group title={t('editor.groups.manager')}>
        <FormRow label={t('editor.fields.managerName')}>{text('managerName')}</FormRow>
        <FormRow label={t('editor.fields.managerPhone')}>{text('managerPhone')}</FormRow>
      </Group>
    </>
  );
}

const TOTAL_CURRENCY_LABEL: Record<CommercialProposalTotalCurrency, string> = {
  RUB: '₽',
  USD: '$',
  CNY: '¥',
};

function TotalCurrencySelect({
  value,
  onChange,
}: {
  value: CommercialProposalTotalCurrency;
  onChange: (value: CommercialProposalTotalCurrency) => void;
}) {
  return (
    <Select
      value={value}
      options={COMMERCIAL_PROPOSAL_TOTAL_CURRENCIES.map((currency) => ({
        value: currency,
        label: TOTAL_CURRENCY_LABEL[currency],
      }))}
      onChange={(next) => {
        const parsed = CommercialProposalTotalCurrencySchema.safeParse(next);
        if (parsed.success) onChange(parsed.data);
      }}
    />
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginBottom: 18 }}>
      <h3
        style={{
          margin: '0 0 8px',
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          color: 'var(--fg-3)',
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function Hint({ text }: { text: string }) {
  return <span style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>{text}</span>;
}

function PaymentSchemeControl({
  value,
  onChange,
}: {
  value: (typeof COMMERCIAL_PROPOSAL_PAYMENT_SCHEMES)[number];
  onChange: (v: (typeof COMMERCIAL_PROPOSAL_PAYMENT_SCHEMES)[number]) => void;
}) {
  const { t } = useTranslation('commercialProposals');
  return (
    <div role="radiogroup" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {COMMERCIAL_PROPOSAL_PAYMENT_SCHEMES.map((scheme) => {
        const selected = scheme === value;
        return (
          <button
            key={scheme}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={`commercial-proposals-scheme-${scheme}`}
            onClick={() => onChange(scheme)}
            style={{
              flex: '1 1 30%',
              minWidth: 120,
              padding: '8px 10px',
              borderRadius: 'var(--r-2)',
              border: `1px solid ${selected ? 'var(--accent-1)' : 'var(--border-1)'}`,
              background: selected ? 'var(--accent-ring)' : 'var(--bg-1)',
              color: 'var(--fg-1)',
              fontSize: 12,
              fontWeight: selected ? 600 : 400,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {t(`editor.paymentScheme.${scheme}`)}
          </button>
        );
      })}
    </div>
  );
}
